import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink, ArrowLeft } from "lucide-react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import FloatingActions from "./FloatingActions";
import { projectsData } from "../data/projectsData";

const AllProjects = ({ isDarkMode, toggleDarkMode }) => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // For AllProjects page, scrollToSection will navigate back to home
  // If we wanted to support direct linking to sections, we'd need more complex logic
  const scrollToSection = (sectionId) => {
    navigate("/");
    // Optional: We could use a hash and handle it in Portfolio.jsx
    // setTimeout(() => {
    //   const element = document.getElementById(sectionId);
    //   if (element) element.scrollIntoView({ behavior: "smooth" });
    // }, 100);
  };

  return (
    <>
      <Navbar
        isDarkMode={isDarkMode}
        toggleDarkMode={toggleDarkMode}
        activeSection="projects" // Highlight projects or nothing
        scrollToSection={scrollToSection}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
      />

      <section
        className={`pt-32 pb-20 min-h-screen ${
          isDarkMode ? "text-white" : "text-gray-900"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <button
              onClick={() => navigate("/")}
              className={`flex items-center gap-2 mb-6 px-4 py-2 rounded-full transition-all duration-300 ${
                isDarkMode
                  ? "bg-gray-800 hover:bg-gray-700 text-gray-300"
                  : "bg-white hover:bg-gray-100 text-gray-600 shadow-sm"
              }`}
            >
              <ArrowLeft size={20} />
              Back to Home
            </button>
            
            <div className="text-center">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                All Projects
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-purple-600 mx-auto mb-6"></div>
              <p
                className={`text-xl max-w-3xl mx-auto ${
                  isDarkMode ? "text-gray-300" : "text-gray-600"
                }`}
              >
                A comprehensive collection of my work and side projects.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projectsData.map((project, index) => (
              <div
                key={index}
                className={`group relative rounded-2xl overflow-hidden transition-all duration-500 hover:-translate-y-2 ${
                  isDarkMode
                    ? "bg-gray-800 shadow-lg shadow-blue-900/20"
                    : "bg-white shadow-xl shadow-blue-100"
                }`}
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold text-gray-900 shadow-lg">
                    {project.category}
                  </div>
                </div>

                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3 group-hover:text-blue-500 transition-colors">
                    {project.title}
                  </h3>
                  <p
                    className={`mb-4 line-clamp-3 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    {project.description}
                  </p>

                  <div className="flex flex-wrap gap-2 mb-6">
                    {project.technologies.map((tech, i) => (
                      <span
                        key={i}
                        className={`px-3 py-1 text-xs font-medium rounded-full transition-colors duration-300 ${
                          isDarkMode
                            ? "bg-gray-800 text-blue-300 border border-gray-700 hover:bg-gray-700"
                            : "bg-blue-50 text-blue-600 border border-blue-100 hover:bg-blue-100"
                        }`}
                      >
                        {tech}
                      </span>
                    ))}
                  </div>

                  <a
                    href={project.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-blue-500 hover:text-blue-600 font-medium transition-colors"
                  >
                    View Project <ExternalLink size={16} />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer isDarkMode={isDarkMode} />
      <FloatingActions />
    </>
  );
};

export default AllProjects;
